/**
 * Users Service
 * Abstraction layer for user management API calls
 */
import { api } from './api'

const mapSubscriptionStatus = (backendStatus) => {
    switch (backendStatus) {
        case 'trial_active':
        case 'trial_pending':
            return 'trial'
        case 'active_paid':
            return 'active'
        case 'cancelled':
        case 'trial_cancelled':
        case 'trial_expired':
            return 'cancelled'
        case 'past_due':
            return 'expired'
        default:
            return backendStatus || 'trial'
    }
}

const DELETION_GRACE_DAYS = 90

const mapUser = (user) => ({
    id: user.id,
    email: user.email,
    username: user.name || user.email?.split('@')[0] || 'Unknown user',
    name: user.name,
    designation: user.designation || null,
    organisation: user.organisation_name || 'No Organisation',
    accountStatus: user.is_active ? 'active' : 'suspended',
    orgAccountStatus: user.account_status || null,
    subscriptionState: mapSubscriptionStatus(user.subscription_status),
    subscriptionPlan: user.subscription_plan || null,
    referralCode: user.referral_code || null,
    referralStatus: user.referral_code ? 'approved' : 'none',
    createdAt: user.created_at,
    updatedAt: user.updated_at,
    lastActive: user.updated_at,
    tenantId: user.tenant_id,
    isAdmin: Boolean(user.is_admin),
    emailVerified: Boolean(user.email_verified),
    isFirstTime: Boolean(user.is_firstTime),
    deletionRequestedAt: user.deletion_requested_at || null,
    deletionScheduledFor: user.deletion_requested_at
        ? new Date(new Date(user.deletion_requested_at).getTime() + DELETION_GRACE_DAYS * 24 * 60 * 60 * 1000).toISOString()
        : null,
    pendingDeletion: Boolean(user.deletion_requested_at),
})

export const usersService = {
    /**
     * Get all users
     * @returns {Promise<Array>}
     */
    async getAll() {
        const res = await api.get('/admin/users')
        if (!res.success) throw new Error('Failed to fetch users')

        return res.data.map(mapUser)
    },

    /**
     * Get a single user profile for admin review
     * @param {string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        const res = await api.get(`/admin/users/${id}`)
        if (!res.success) throw new Error('Failed to fetch user profile')

        return {
            user: mapUser(res.data.user),
            organisation: res.data.organisation || null,
            providerProfile: res.data.providerProfile || null,
            teamMembers: res.data.teamMembers || [],
        }
    },

    /**
     * Update user details via admin
     * @param {string} id
     * @param {Object} updates - { accountStatus, subscriptionState, referralStatus, notes }
     * @returns {Promise<Object>}
     */
    async updateDetails(id, updates) {
        const res = await api.put(`/admin/users/${id}`, updates)
        if (!res.success) throw new Error('Failed to update user')
        return res.data
    },

    /**
     * Cancel a pending account deletion (admin action)
     * @param {string} id
     * @returns {Promise<Object>}
     */
    async cancelDeletion(id) {
        const res = await api.post(`/admin/users/${id}/cancel-deletion`)
        if (!res.success) throw new Error('Failed to cancel deletion')
        return res.data
    },

    /**
     * Full account-status transition history for an organisation, for reporting.
     * @param {string} orgId
     * @returns {Promise<Array>}
     */
    async getAccountHistory(orgId) {
        const res = await api.get(`/account/history/${orgId}`)
        if (!res.success) throw new Error('Failed to fetch account history')
        return res.data
    },
}
